import React from 'react';
import { LoadingIndicator } from 'library';
import { AutoComplete, Link, Row, Stack, Text } from 'library/ui';
import { ProviderSelection } from 'parent-portal/forms/JobRequestTypes';
import { useJobFormContext } from 'parent-portal/forms/JobFormContext';
import type { ExtendedProviderSelection } from './utilities';

interface Props {
    selected: ProviderSelection[];
    onChange: (selectedProviderUserIds: ProviderSelection[]) => void;
    existingMatches?: ProviderSelection[];
    multiple?: boolean;
    clearOtherInput?: () => void;
}

function statusTone(status?: string): 'error' | 'warning' | 'secondary' {
    const normalized = status?.toLowerCase();
    if (normalized === 'unavailable') return 'error';
    if (normalized === 'out_of_compliance') return 'warning';
    return 'secondary';
}

export default function PreferredWorkersInput({
    selected,
    onChange,
    existingMatches,
    multiple = true,
    clearOtherInput,
}: Props) {
    const { preferredWorkers, loadingPreferredWorkers, preferredWorkersHasNextPage, loadMorePreferredWorkers } =
        useJobFormContext();

    const additionalSelectedOptions: ExtendedProviderSelection[] = selected.filter(
        (sel) => !preferredWorkers.some((preferred) => preferred.user.id === sel.id),
    );
    const options: ExtendedProviderSelection[] = [
        ...additionalSelectedOptions,
        ...preferredWorkers.map((worker) => ({
            id: worker.user.id,
            name: `${worker.user.first_name} ${worker.user.last_name}`,
            status: worker.status,
            sanitizedStatus: worker.status ? `(${worker.status.replaceAll('_', ' ').capitalize()})` : '',
        })),
    ];

    const isDisabled = (option: ExtendedProviderSelection) =>
        !!existingMatches?.some((match) => match.id === option.id) || option.status === 'out_of_compliance';

    const selectedValues = options.filter(
        (option) => selected.some((s) => s.id === option.id) || existingMatches?.some((m) => m.id === option.id),
    );

    const onSelectChange = (value: ExtendedProviderSelection | ExtendedProviderSelection[] | null) => {
        const chosen = value ? (Array.isArray(value) ? value : [value]) : [];
        const withoutExisting = chosen.filter((it) => !existingMatches?.some((m) => m.id === it.id));
        if (!multiple && withoutExisting.length > 0) clearOtherInput?.();
        onChange(withoutExisting);
    };

    const renderContent = () => {
        if (loadingPreferredWorkers && preferredWorkers.length === 0) {
            return <LoadingIndicator />;
        }
        if (preferredWorkers.length === 0) {
            return <Text>It looks like you have not set any preferred workers.</Text>;
        }
        return (
            <>
                <AutoComplete<ExtendedProviderSelection, boolean>
                    multiple={multiple}
                    options={options}
                    selected={multiple ? selectedValues : selectedValues[0] ?? null}
                    onSelect={onSelectChange}
                    getOptionLabel={(option) => option.name}
                    getOptionSelected={(option, value) => option.id === value.id}
                    getOptionDisabled={isDisabled}
                    renderOption={(option) => (
                        <Row gap="sm" align="center">
                            <Text>{option.name}</Text>
                            {option.sanitizedStatus ? (
                                <Text size="xs" weight="bold" tone={statusTone(option.status)}>
                                    {option.sanitizedStatus}
                                </Text>
                            ) : null}
                        </Row>
                    )}
                    placeholder="Click here to search for preferred workers"
                />
                {!loadingPreferredWorkers && preferredWorkersHasNextPage && (
                    <Link onClick={loadMorePreferredWorkers}>load more</Link>
                )}
            </>
        );
    };

    return (
        <Stack gap="sm">
            <Text weight="bold">Preferred Workers</Text>
            {renderContent()}
        </Stack>
    );
}
